import intl from 'react-intl-universal';
import { Button } from '@getsynapse/design-system';
import sparkles from 'assets/icons/sparkles-current-color.svg';
import PageTitle from 'Molecules/PageTitle/PageTitle';
import DashboardsList from './components/DashboardsList';
import { useCallback, useEffect, useState } from 'react';
import { useAppThunkDispatch } from 'state/store';
import {
  dashboardSelectors,
  getDashboardById,
  getDashboards,
  setCurrentDashboardId,
} from 'state/normalized/dashboard/dashboardSlice';
import { getAgentDashboards } from 'state/normalized/agentDashboard/agentDashboardSlice';
import CreateDashboardModal from './components/CreateDashboard/CreateDashboardModal';
import EditDashboardNameModal from './components/EditDashboard/EditDashboardNameModal';
import { useHistory } from 'react-router-dom';
import { PATHS, V2_PATHS } from 'utils/constants';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';

const DashboardsListPage = () => {
  const dispatch = useAppThunkDispatch();
  const [isCreateDashboardModalOpen, setIsCreateDashboardModalOpen] =
    useState(false);
  const [editDashboardNameModalOpen, setEditDashboardNameModalOpen] =
    useState(false);

  const { canCreate: canCreateDashboard } = useSelector(
    dashboardSelectors.selectGlobalAffordanceFlags
  );

  const { reportingAgentAccess = false } = useFlags();

  const history = useHistory();

  useEffect(() => {
    dispatch(getDashboards());
  }, [dispatch]);

  useEffect(() => {
    if (reportingAgentAccess) {
      dispatch(getAgentDashboards());
    }
  }, [dispatch, reportingAgentAccess]);

  const handleCreateDashboard = () => {
    setIsCreateDashboardModalOpen(true);
  };

  const handleEditDashboardName = useCallback(
    (dashboardId: string) => {
      dispatch(setCurrentDashboardId(dashboardId));
      dispatch(getDashboardById({ dashboardId }));
      setEditDashboardNameModalOpen(true);
    },
    [dispatch]
  );

  const handleAfterEditDashboard = useCallback(() => {
    dispatch(getDashboards());
  }, [dispatch]);

  return (
    <div className='flex flex-col h-full'>
      <PageTitle
        titleComponent={intl.get('DASHBOARDS_LIST_PAGE.TITLE')}
        headerChildren={
          canCreateDashboard && (
            <div className='flex items-center space-x-2'>
              {reportingAgentAccess && (
                <a href={V2_PATHS.REPORTING_AGENT_PAGE}>
                  <Button variant='secondary' iconSrc={sparkles}>
                    {intl.get(
                      'DASHBOARDS_LIST_PAGE.CREATE_WITH_REPORTING_AGENT'
                    )}
                  </Button>
                </a>
              )}
              <Button
                onClick={handleCreateDashboard}
                data-cy='create-dashboard-button'
              >
                {intl.get('DASHBOARDS_LIST_PAGE.CREATE_DASHBOARD_BUTTON')}
              </Button>
            </div>
          )
        }
      />
      <div className='relative w-full h-full'>
        <CreateDashboardModal
          isOpen={isCreateDashboardModalOpen}
          afterCreateDashboard={(id: string) => {
            history.push(`${PATHS.DASHBOARD_PAGE}/${id}`);
          }}
          onCloseModal={() => setIsCreateDashboardModalOpen(false)}
        />
        <EditDashboardNameModal
          isOpen={editDashboardNameModalOpen}
          afterEditDashboard={handleAfterEditDashboard}
          onCloseModal={() => setEditDashboardNameModalOpen(false)}
        />
        <div className='relative w-full h-full'>
          <DashboardsList
            onEditDashboardName={handleEditDashboardName}
            onCreateDashboard={handleCreateDashboard}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardsListPage;
